import React, { useState, useEffect, useRef } from "react";
import { Box, TextField, InputAdornment, SxProps } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";

// ==================== TYPES ====================
export type SortOrder = "oldest" | "newest";

export interface SortInputToggleProps {
  sortOrder?: SortOrder;
  onChange?: (sortOrder: SortOrder) => void;
  disabled?: boolean;
  customPlaceholderSx?: SxProps;
  fullWidth?: boolean;
}

// ==================== CONSTANTS ====================
const BORDER_COLOR_DEFAULT = "#cfd8dc";
const DISPLAY_TEXT = {
  oldest: "Sort by oldest to newest",
  newest: "Sort by newest to oldest",
} as const;

// ==================== COMPONENT ====================
const SortInputToggle: React.FC<SortInputToggleProps> = ({
  sortOrder: controlledSortOrder,
  onChange,
  disabled = false,
  customPlaceholderSx,
  fullWidth = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalSortOrder, setInternalSortOrder] =
    useState<SortOrder>("oldest");

  const isControlled = controlledSortOrder !== undefined;
  const sortOrder = isControlled ? controlledSortOrder : internalSortOrder;

  useEffect(() => {
    if (isControlled && controlledSortOrder !== undefined) {
      setInternalSortOrder(controlledSortOrder);
    }
  }, [controlledSortOrder, isControlled]);

  const handleClick = () => {
    if (disabled) return;

    const newSortOrder: SortOrder =
      sortOrder === "oldest" ? "newest" : "oldest";

    if (!isControlled) {
      setInternalSortOrder(newSortOrder);
    }

    onChange?.(newSortOrder);

    setTimeout(() => {
      inputRef.current?.blur();
    }, 500);
  };

  // ==================== DERIVED VALUES ====================
  const displayText = DISPLAY_TEXT[sortOrder];
  const ArrowIcon =
    sortOrder === "oldest" ? ArrowDownwardIcon : ArrowUpwardIcon;

  // ==================== RENDER ====================
  return (
    <Box sx={{ width: fullWidth ? "100%" : "auto", ...customPlaceholderSx }}>
      <TextField
        value={displayText}
        variant="outlined"
        size="small"
        fullWidth={fullWidth}
        disabled={disabled}
        onClick={handleClick}
        inputRef={inputRef}
        sx={{
          backgroundColor: "white",
          width: "100%",
          cursor: disabled ? "not-allowed" : "pointer",

          // Input root styles
          "& .MuiOutlinedInput-root": {
            borderRadius: nawyTheme.nawy.radius.lg,
            paddingRight: nawyTheme.nawy.spacing.xxs,
            height: "36px",
            fontSize:
              nawyTheme.nawy.typography.english.variants.body.md.regular
                .fontSize,
            fontFamily: nawyTheme.nawy.typography.english.fontFamily,
            fontWeight:
              nawyTheme.nawy.typography.english.variants.body.md.regular
                .fontWeight,
            cursor: disabled ? "not-allowed" : "pointer",

            // Border styles
            "& fieldset": {
              borderColor: BORDER_COLOR_DEFAULT,
            },
            "&:hover fieldset": {
              borderColor: disabled
                ? undefined
                : nawyTheme.nawy.colors.semantic.border.extension,
            },
            "&.Mui-focused fieldset": {
              borderColor: disabled
                ? undefined
                : nawyTheme.nawy.colors.semantic.border.extension,
            },
          },

          // Input element styles
          "& .MuiInputBase-input": {
            padding: `${nawyTheme.nawy.spacing.xs} 0 ${nawyTheme.nawy.spacing.xs} ${nawyTheme.nawy.spacing.md}`,
            fontSize:
              nawyTheme.nawy.typography.english.variants.body.md.regular
                .fontSize,
            fontFamily: nawyTheme.nawy.typography.english.fontFamily,
            fontWeight:
              nawyTheme.nawy.typography.english.variants.body.md.regular
                .fontWeight,
            cursor: disabled ? "not-allowed" : "pointer",
          },

          // Disabled state styles
          "& .MuiInputBase-input[disabled]": {
            cursor: "not-allowed",
            WebkitTextFillColor: "rgba(0, 0, 0, 0.38)",
          },
        }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment
              position="end"
              sx={{
                color: nawyTheme.nawy.colors.semantic.text.primary,
                marginRight: nawyTheme.nawy.spacing.sm,
              }}
            >
              <ArrowIcon
                sx={{
                  fontSize:
                    nawyTheme.nawy.typography.english.variants.body.lg.regular
                      .fontSize,
                }}
              />
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};

export default SortInputToggle;
